import { create } from "zustand";
import { persist } from "zustand/middleware";
import { blogPostService } from "@/services/blogPostService";
import {
  BlogPost,
  CreateBlogPostDto,
  UpdateBlogPostDto,
  GetPaginationBlogPostsDto,
} from "@/types/blog-post";
import { showToast } from "@/lib/toast";

interface BlogPostState {
  // State
  blogPosts: BlogPost[];
  selectedBlogPost: BlogPost | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  isLoadingDetail: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchBlogPosts: (params?: GetPaginationBlogPostsDto) => Promise<void>;
  fetchBlogPostById: (id: string) => Promise<void>;
  createBlogPost: (data: FormData | CreateBlogPostDto) => Promise<void>;
  updateBlogPost: (
    id: string,
    data: FormData | UpdateBlogPostDto
  ) => Promise<void>;
  deleteBlogPost: (id: string) => Promise<void>;
  changeStatus: (id: string, status: string) => Promise<void>;
  toggleDisplay: (id: string) => Promise<void>;
  setSelectedBlogPost: (blogPost: BlogPost | null) => void;
  clearSelectedBlogPost: () => void;
  clearError: () => void;
  setCurrentPage: (page: number) => void;
}

export const useBlogPostStore = create<BlogPostState>()(
  persist(
    (set, get) => ({
      // Initial state
      blogPosts: [],
      selectedBlogPost: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      isLoading: false,
      isLoadingDetail: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      error: null,

      // Fetch blogPosts with pagination
      fetchBlogPosts: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response: any = await blogPostService.getPaginationBlogPosts(
            params
          );
          if (response.ok && response.data) {
            set({
              blogPosts: response.data?.data?.data.blogPosts,
              pagination: response.data?.data?.data.pagination,
              isLoading: false,
            });
          } else {
            set({
              error: response.data?.message || "Lỗi khi tải danh sách bài viết",
              isLoading: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải danh sách bài viết",
            isLoading: false,
          });
        }
      },

      // Fetch blog post by ID
      fetchBlogPostById: async (id: string) => {
        set({ isLoadingDetail: true, error: null });
        try {
          const response: any = await blogPostService.getBlogPostById(id);
          if (response.ok && response.data) {
            set({
              selectedBlogPost: response.data?.data?.data,
              isLoadingDetail: false,
            });
          } else {
            set({
              error: response.data?.message || "Lỗi khi tải bài viết",
              isLoadingDetail: false,
            });
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tải bài viết",
            isLoadingDetail: false,
          });
        }
      },

      // Create blog post
      createBlogPost: async (data: FormData | CreateBlogPostDto) => {
        set({ isCreating: true, error: null });
        try {
          const response: any = await blogPostService.create(data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchBlogPosts({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isCreating: false });
            showToast.success("Tạo bài viết thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi tạo bài viết",
              isCreating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi tạo bài viết");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi tạo bài viết",
            isCreating: false,
          });
          showToast.error(error.message || "Lỗi khi tạo bài viết");
        }
      },

      // Update blog post
      updateBlogPost: async (
        id: string,
        data: FormData | UpdateBlogPostDto
      ) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await blogPostService.updateBlogPost(id, data);
          if (response.ok && response.data) {
            const { pagination } = get();
            await get().fetchBlogPosts({
              page: pagination.page,
              limit: pagination.limit,
            });
            set({ isUpdating: false });
            showToast.success("Cập nhật bài viết thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi cập nhật bài viết",
              isUpdating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi cập nhật bài viết");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi cập nhật bài viết",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi cập nhật bài viết");
        }
      },

      // Delete blog post
      deleteBlogPost: async (id: string) => {
        set({ isDeleting: true, error: null });
        try {
          const response: any = await blogPostService.deleteBlogPost(id);
          if (response.ok) {
            const { blogPosts } = get();
            const filteredBlogPosts = blogPosts.filter(
              (blogPost) => blogPost._id !== id
            );
            const { pagination } = get();
            set({
              blogPosts: filteredBlogPosts,
              pagination: {
                ...pagination,
                total: pagination.total - 1,
              },
              selectedBlogPost: null,
              isDeleting: false,
            });
            showToast.success("Xóa bài viết thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi xóa bài viết",
              isDeleting: false,
            });
            showToast.error(response.data?.message || "Lỗi khi xóa bài viết");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi xóa bài viết",
            isDeleting: false,
          });
          showToast.error(error.message || "Lỗi khi xóa bài viết");
        }
      },

      // Change blog post status
      changeStatus: async (id: string, status: string) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await blogPostService.changeStatus(id, status);
          if (response.ok && response.data) {
            const { blogPosts } = get();
            const updatedBlogPosts = blogPosts.map((blogPost) =>
              blogPost._id === id ? response.data : blogPost
            );
            set({
              blogPosts: updatedBlogPosts,
              selectedBlogPost: response.data,
              isUpdating: false,
            });
            showToast.success("Thay đổi trạng thái thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi thay đổi trạng thái",
              isUpdating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi thay đổi trạng thái");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi thay đổi trạng thái",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi thay đổi trạng thái");
        }
      },

      // Toggle blog post display
      toggleDisplay: async (id: string) => {
        set({ isUpdating: true, error: null });
        try {
          const response: any = await blogPostService.toggleDisplay(id);
          if (response.ok && response.data) {
            const { blogPosts } = get();
            const updatedBlogPosts = blogPosts.map((blogPost) =>
              blogPost._id === id ? response.data : blogPost
            );
            set({
              blogPosts: updatedBlogPosts,
              selectedBlogPost: response.data,
              isUpdating: false,
            });
            showToast.success("Thay đổi hiển thị thành công");
          } else {
            set({
              error: response.data?.message || "Lỗi khi thay đổi hiển thị",
              isUpdating: false,
            });
            showToast.error(response.data?.message || "Lỗi khi thay đổi hiển thị");
          }
        } catch (error: any) {
          set({
            error: error.message || "Lỗi khi thay đổi hiển thị",
            isUpdating: false,
          });
          showToast.error(error.message || "Lỗi khi thay đổi hiển thị");
        }
      },

      // Set selected blog post
      setSelectedBlogPost: (blogPost: BlogPost | null) => {
        set({ selectedBlogPost: blogPost });
      },

      // Clear selected blog post
      clearSelectedBlogPost: () => {
        set({ selectedBlogPost: null });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set current page
      setCurrentPage: (page: number) => {
        set((state) => ({
          pagination: { ...state.pagination, page },
        }));
      },
    }),
    {
      name: "blog-post-storage",
      partialize: (state) => ({
        pagination: state.pagination,
      }),
    }
  )
);
